import Twemoji from "@src/Twemoji";
import React, {
  createContext,
  useContext,
  ReactNode,
  useCallback,
} from "react";

import { DSCopyButton } from "./DSCopyButton";
import { NoteDisplayData } from "./DSNoteDisplay";
import { DSDiffView } from "./DSNoteInputDiffView";
import { DSRichTextarea } from "../molecules/DSRichTextarea";

// ============================================================================
// Types
// ============================================================================

export type DSNoteInputSize = "default" | "compact";

/** All available note sections */
export type NoteSection =
  | "subjective"
  | "objective"
  | "assessment"
  | "plan"
  | "additional"
  | "freeform";

/**
 * Diff state for a single section, used to show AI-generated changes.
 */
export interface DiffState {
  /** The original content before AI modification */
  before: string;
  /** The AI-generated content. When null, diff mode is inactive. */
  after: string | null;
  /** Whether content is currently loading/streaming */
  isLoading?: boolean;
}

// Section configuration type
export interface SectionConfig {
  letter: string | null;
  label: string;
  field: keyof NoteDisplayData;
  bgClass: string;
  borderClass: string;
  textClass: string;
  toolbarBgClass: string;
  toolbarBorderClass: string;
}

// Context for sharing state between compound components
interface NoteInputContextValue {
  data: NoteDisplayData;
  onChange: (data: NoteDisplayData) => void;
  size: DSNoteInputSize;
  disabled: boolean;
  placeholder?: string;
  showPlaceholderAlways: boolean;
  dataTestId: string;
  plainText: boolean;
}

const NoteInputContext = createContext<NoteInputContextValue | null>(
  null,
);

const useNoteInputContext = () => {
  const context = useContext(NoteInputContext);
  if (!context) {
    throw new Error(
      "DSNoteInput compound components must be used within DSNoteInput",
    );
  }
  return context;
};

// Main component props
export interface DSNoteInputProps {
  /** The note/template data */
  data: NoteDisplayData;
  /** Called when any field changes - returns the full updated data object */
  onChange: (data: NoteDisplayData) => void;
  /** Size variant - compact uses smaller text and tighter spacing */
  size?: DSNoteInputSize;
  /** Optional placeholder text for empty fields */
  placeholder?: string;
  /** Disable all inputs */
  disabled?: boolean;
  /** Test ID for automated testing */
  dataTestId: string;
  /** Children (NoteField subcomponents) */
  children: ReactNode;
  /** Show placeholder even when fields are not focused (default: false) */
  showPlaceholderAlways?: boolean;
  /** Mouse up handler (used for Select/Replace text selection) */
  onMouseUp?: (e: React.MouseEvent) => void;
  /** Use plain text mode: no markdown, single-newline I/O, no toolbar */
  plainText?: boolean;
}

// ============================================================================
// Constants
// ============================================================================

export const sectionConfig: Record<NoteSection, SectionConfig> = {
  subjective: {
    letter: "S",
    label: "Subjective",
    field: "subjective",
    bgClass: "bg-gradient-subjective",
    toolbarBgClass: "bg-subjective",
    toolbarBorderClass: "",
    borderClass:
      "border-subjective-subtle focus-within:border-subjective",
    textClass: "text-subjective",
  },
  objective: {
    letter: "O",
    label: "Objective",
    field: "objective",
    bgClass: "bg-gradient-objective",
    toolbarBgClass: "bg-objective",
    toolbarBorderClass: "",
    borderClass:
      "border-objective-subtle focus-within:border-objective",
    textClass: "text-objective",
  },
  assessment: {
    letter: "A",
    label: "Assessment",
    field: "assessment",
    bgClass: "bg-gradient-assessment",
    toolbarBgClass: "bg-assessment",
    toolbarBorderClass: "",
    borderClass:
      "border-assessment-subtle focus-within:border-assessment",
    textClass: "text-assessment",
  },
  plan: {
    letter: "P",
    label: "Plan",
    field: "plan",
    bgClass: "bg-gradient-plan",
    toolbarBgClass: "bg-plan",
    toolbarBorderClass: "",
    borderClass: "border-plan-subtle focus-within:border-plan",
    textClass: "text-plan",
  },
  additional: {
    letter: "+",
    label: "Additional Notes",
    field: "additionalNotes",
    bgClass: "bg-secondary",
    borderClass: "border-additional-subtle focus-within:border-brand",
    toolbarBgClass: "bg-secondary",
    toolbarBorderClass: "",
    textClass: "text-additional",
  },
  freeform: {
    letter: null,
    label: "",
    field: "content",
    bgClass: "bg-gradient-freeform",
    borderClass: "border-freeform-subtle focus-within:border-brand",
    toolbarBgClass: "bg-brand-subtle",
    toolbarBorderClass: "",
    textClass: "text-freeform",
  },
};

/** Default SOAP sections in standard order */
export const SOAP_SECTIONS: NoteSection[] = [
  "subjective",
  "objective",
  "assessment",
  "plan",
  "additional",
];

// ============================================================================
// Root Component
// ============================================================================

export const DSNoteInput = ({
  data,
  onChange,
  size = "default",
  placeholder = "Enter text here...",
  disabled = false,
  dataTestId,
  children,
  showPlaceholderAlways = true,
  onMouseUp,
  plainText = false,
}: DSNoteInputProps) => {
  return (
    <NoteInputContext.Provider
      value={{
        data,
        onChange,
        size,
        disabled,
        placeholder,
        showPlaceholderAlways,
        dataTestId,
        plainText,
      }}
    >
      <div
        className="flex flex-col gap-md w-full"
        data-testid={dataTestId}
        data-intercom-target={dataTestId}
        role="form"
        aria-label="Clinical note input"
        onMouseUp={onMouseUp}
      >
        {children}
      </div>
    </NoteInputContext.Provider>
  );
};

// ============================================================================
// Internal Components
// ============================================================================

interface SparkleButtonProps {
  onClick: () => void;
  loading?: boolean;
  dataTestId: string;
}

const SparkleButton = ({
  onClick,
  loading = false,
  dataTestId,
}: SparkleButtonProps) => {
  return (
    <button
      type="button"
      className={`h-8 w-8 flex items-center justify-center p-1.5 rounded-full border-2 transition-all ${
        loading
          ? "bg-disabled cursor-not-allowed border-disabled"
          : "cursor-pointer shadow-md bg-brand border-brand hover:brightness-90"
      }`}
      onClick={onClick}
      disabled={loading}
      title="Trigger Writing Assistant"
      aria-label="Trigger Writing Assistant"
      data-testid={dataTestId}
      data-intercom-target={dataTestId}
    >
      <Twemoji className="text-xl z-20" emoji="✨" />
    </button>
  );
};

// ============================================================================
// NoteField Subcomponent
// ============================================================================

/** Base props for NoteField */
interface NoteFieldBaseProps {
  /** Which section to render */
  section: NoteSection;
  /** Optional custom placeholder for this field */
  placeholder?: string;
  /** Show copy button */
  showCopyButton?: boolean;
  /** Custom label override (useful for freeform) */
  label?: string;
  /** Extra action buttons rendered before the copy button */
  actions?: ReactNode;
}

/** Props when sparkle button is shown - requires onSparkleClick */
type NoteFieldSparkleProps =
  | {
      showSparkleButton: true;
      onSparkleClick: () => void;
      sparkleLoading?: boolean;
    }
  | {
      showSparkleButton?: false;
      onSparkleClick?: undefined;
      sparkleLoading?: undefined;
    };

/** Props when diff state is active - requires callbacks */
type NoteFieldDiffProps =
  | {
      diffState: DiffState;
      onDiffAccept: () => void;
      onDiffReject: () => void;
    }
  | {
      diffState?: undefined;
      onDiffAccept?: undefined;
      onDiffReject?: undefined;
    };

type NoteFieldProps = NoteFieldBaseProps &
  NoteFieldSparkleProps &
  NoteFieldDiffProps;

const NoteField = ({
  section,
  placeholder: fieldPlaceholderProp,
  showCopyButton = true,
  showSparkleButton = false,
  onSparkleClick,
  sparkleLoading = false,
  diffState,
  onDiffAccept,
  onDiffReject,
  label,
  actions,
}: NoteFieldProps) => {
  const {
    data,
    onChange,
    size,
    disabled,
    placeholder: contextPlaceholder,
    showPlaceholderAlways,
    dataTestId,
    plainText,
  } = useNoteInputContext();

  const isCompact = size === "compact";
  const minHeight = isCompact ? "100px" : "150px";

  const config = sectionConfig[section];
  const fieldValue = (data[config.field] as string) || "";
  const fieldPlaceholder =
    fieldPlaceholderProp ||
    contextPlaceholder ||
    `Enter ${config.label.toLowerCase()}...`;
  const displayLabel = label || config.label;

  // Check if this section is in diff mode
  const isInDiffMode =
    diffState?.after !== null && diffState?.after !== undefined;

  // Whether to show the letter badge (compact mode + section has a letter)
  const showLetterBadge = isCompact && config.letter !== null;

  // Handler for updating this field
  const handleFieldChange = useCallback(
    (value: string) => {
      onChange({
        ...data,
        [config.field]: value,
      });
    },
    [data, onChange, config.field],
  );

  return (
    <div data-testid={`${dataTestId}-${section}`}>
      {isInDiffMode && diffState ? (
        // Diff mode: Show DSDiffView with integrated toolbar
        <DSDiffView
          afterContent={diffState.after}
          beforeContent={diffState.before}
          section={section}
          label={displayLabel}
          isCompact={isCompact}
          isLoading={diffState.isLoading}
          onAccept={onDiffAccept}
          onReject={onDiffReject}
          dataTestId={`${dataTestId}-${section}-diff`}
          plainText={plainText}
        />
      ) : (
        // Normal mode: Show regular textarea with buttons
        <div
          className={
            showLetterBadge ? `flex flex-row items-start gap-sm` : ""
          }
        >
          {/* Letter badge for compact mode */}
          {showLetterBadge && (
            <span
              className={`text-xl font-bold shrink-0 ${config.textClass}`}
              aria-label={displayLabel}
            >
              {config.letter}
            </span>
          )}

          <div className="flex-1 min-w-0 relative">
            {/* Action buttons container */}
            {(showCopyButton || showSparkleButton || actions) && (
              <div className="absolute top-2 right-2 z-10 flex gap-sm">
                {actions}
                {showSparkleButton && onSparkleClick && (
                  <SparkleButton
                    onClick={onSparkleClick}
                    loading={sparkleLoading}
                    dataTestId={`${dataTestId}-${section}-sparkle-btn`}
                  />
                )}
                {showCopyButton && (
                  <DSCopyButton
                    text={fieldValue}
                    dataTestId={`${dataTestId}-${section}-copy-btn`}
                  />
                )}
              </div>
            )}

            <DSRichTextarea
              key={String(plainText)}
              value={fieldValue}
              onChange={handleFieldChange}
              placeholder={fieldPlaceholder}
              disabled={disabled}
              size={isCompact ? "sm" : "md"}
              dataTestId={`${dataTestId}-${section}-textarea`}
              bgClass={config.bgClass}
              borderClass={config.borderClass}
              toolbarBgClass={config.toolbarBgClass}
              toolbarBorderClass={config.toolbarBorderClass}
              showPlaceholderAlways={showPlaceholderAlways}
              plainText={plainText}
            >
              {/* Only show toolbar in default size */}
              {!isCompact && (
                <DSRichTextarea.Toolbar
                  textareaTitle={displayLabel}
                  hideFormatting={plainText}
                />
              )}
              <DSRichTextarea.Input minHeight={minHeight} />
            </DSRichTextarea>
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// Attach Subcomponents
// ============================================================================

DSNoteInput.NoteField = NoteField;
